/** @file slotWarnings: the lint warnings about slots and pivots, each with the slot "jump to" selects. */
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Warning } from '@zilo/pose3d-format/types/warning';

/** A warning the Art screen lists and the slot it is about (null when it names none, e.g. a summary). */
export type SlotWarning = { warning: Warning; slot: string | null };

const CODES = new Set<Warning['code']>(['EMPTY_SLOT', 'PIVOT_OUTSIDE', 'IMAGE_RESIZED']);

/** The first slot (by drawable, entry, tile) whose entry holds image `image`. */
function slotOfImage(puppet: Puppet, image: unknown): string | null {
  for (const d of puppet.drawables) {
    if (d.kind !== 'impostor') continue;
    for (const [e, entry] of d.facets.entries()) {
      const tile = Object.keys(entry.slots).find((t) => entry.slots[t] === image);
      if (tile !== undefined) return slotKey(d.id, e, Number(tile));
    }
  }
  return null;
}

/** The slot a warning names: `params.slot`, entry tile 0 of `params.drawable`/`entry`, or the slot of `params.image`. */
function slotOf(puppet: Puppet, w: Warning): string | null {
  const p = w.params ?? {};
  if (typeof p['slot'] === 'string') return p['slot'];
  if (typeof p['drawable'] === 'string' && typeof p['entry'] === 'number') return slotKey(p['drawable'], p['entry'], 0);
  return p['image'] === undefined ? null : slotOfImage(puppet, p['image']);
}

/** `EMPTY_SLOT`, `PIVOT_OUTSIDE` and `IMAGE_RESIZED` warnings of `lint`, in order, with the slot each one is about. */
export function slotWarnings(lint: readonly Warning[], puppet: Puppet): SlotWarning[] {
  return lint.filter((w) => CODES.has(w.code)).map((warning) => ({ warning, slot: slotOf(puppet, warning) }));
}
